import React, { memo, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { JournalEntry } from "./JournalEntry";
import { JournalView } from "./JournalView";
import { JournalEntryType, JournalEntry as JournalEntryInterface } from "../../types/journal";

// Interface for the component props
interface JournalContentProps {
    isLoading: boolean;
    isToday: boolean;
    isEditing: boolean;
    activeTab: JournalEntryType;
    selectedDate: Date;
    existingEntry: JournalEntryInterface | null;
    handleSaveEntry: (data: { content: string; metrics: Record<string, number> }) => Promise<void>;
    onEdit: () => void;
}

// Animation variants for smoother transitions
const contentAnimations = {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -10 },
    transition: { duration: 0.25 }
};

// Loading spinner component
const LoadingSpinner = () => (
    <div className="absolute inset-0 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
    </div>
);

// Main JournalContent component wrapped with memo for performance
export const JournalContent: React.FC<JournalContentProps> = memo(({
    isLoading,
    isToday,
    isEditing,
    activeTab,
    selectedDate,
    existingEntry,
    handleSaveEntry,
    onEdit
}) => {
    const [contentReady, setContentReady] = useState(false);

    useEffect(() => {
        setContentReady(false);

        const timer = setTimeout(() => {
            setContentReady(true);
        }, 100);

        return () => {
            clearTimeout(timer);
        };
    }, [existingEntry, activeTab, selectedDate]);

    if (isLoading || !contentReady) {
        return <LoadingSpinner />;
    }

    const showEditableForm = isToday || isEditing;

    return (
        <AnimatePresence mode="wait">
            <motion.div
                key={`${activeTab}-${isEditing}-${selectedDate.toISOString()}-${existingEntry?.id || 'new'}`}
                {...contentAnimations}
                className="min-h-[300px]"
            >
                {showEditableForm ? (
                    <JournalEntry
                        key={`entry-${existingEntry?.id || 'new'}-${activeTab}`}
                        type={activeTab}
                        date={selectedDate}
                        existingEntry={existingEntry}
                        onSave={handleSaveEntry}
                        readonly={false}
                    />
                ) : (
                    <JournalView
                        type={activeTab}
                        entry={existingEntry}
                        onEdit={onEdit}
                    />
                )}
            </motion.div>
        </AnimatePresence>
    );
});

// Add display name for debugging
JournalContent.displayName = "JournalContent";

export default JournalContent;